import { useCallback, useEffect, useEffectEvent, useRef, useState } from "react";
import type { ApiClient } from "../../api/client";
import type { LiveMessage, Order } from "../../api/types";

export interface OrdersState {
  orders: Order[];
  loaded: boolean;
  /** Ids of orders that just arrived or changed, to highlight them briefly. */
  recent: ReadonlySet<string>;
  reload: () => void;
  apply: (message: LiveMessage) => void;
  /** Put a changed order in the list right away (the live update follows). */
  upsert: (order: Order) => void;
}

const HIGHLIGHT_MS = 1_600;

/** What arrived live while the list was being fetched: the order, or "deleted". */
export type LiveChanges = ReadonlyMap<string, Order | "deleted">;

/**
 * The fetched list plus what arrived live while it was on its way, so an order created or changed
 * during a (re)load is never lost from the screen. A newer revision always wins.
 */
export function mergeLive(list: readonly Order[], changes: LiveChanges): Order[] {
  const byId = new Map(list.map((order) => [order.id, order]));
  for (const [id, change] of changes) {
    if (change === "deleted") {
      byId.delete(id);
      continue;
    }
    const known = byId.get(id);
    if (known === undefined || known.revision < change.revision) byId.set(id, change);
  }
  return [...byId.values()].sort((a, b) => b.number - a.number);
}

/** Today's orders, kept current by live updates; a full reload after any doubt (resync). */
export function useOrders(api: ApiClient, onError: (error: unknown) => void): OrdersState {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [recent, setRecent] = useState<ReadonlySet<string>>(new Set());
  const [generation, setGeneration] = useState(0);
  const reportError = useEffectEvent(onError);
  /** Live changes that arrive while the list is loading; merged in when it arrives (Furkan, 01/10/2026). */
  const duringLoad = useRef<Map<string, Order | "deleted"> | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const changes = new Map<string, Order | "deleted">();
    duringLoad.current = changes;
    api
      .request<Order[]>("GET", "/orders", { retry: true, signal: controller.signal })
      .then((list) => {
        if (duringLoad.current === changes) duringLoad.current = null;
        setOrders(mergeLive(list, changes));
        setLoaded(true);
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) reportError(error);
      });
    return () => {
      controller.abort();
    };
  }, [api, generation]);

  const highlight = useCallback((id: string) => {
    setRecent((current) => new Set(current).add(id));
    setTimeout(() => {
      setRecent((current) => {
        const next = new Set(current);
        next.delete(id);
        return next;
      });
    }, HIGHLIGHT_MS);
  }, []);

  const upsert = useCallback((saved: Order) => {
    // what this till's own change printed is told once (toasts); the list keeps the order itself
    const { prints: _printed, ...order } = saved;
    const pending = duringLoad.current?.get(order.id);
    if (duringLoad.current && !(pending !== undefined && pending !== "deleted" && pending.revision > order.revision)) {
      duringLoad.current.set(order.id, order);
    }
    setOrders((current) => {
      const index = current.findIndex((candidate) => candidate.id === order.id);
      if (index < 0) return [order, ...current].sort((a, b) => b.number - a.number);
      // never let an older revision overwrite a newer one (messages can cross)
      if (current[index]!.revision > order.revision) return current;
      return current.map((candidate) => (candidate.id === order.id ? order : candidate));
    });
  }, []);

  const reload = useCallback(() => {
    setGeneration((value) => value + 1);
  }, []);

  const apply = useCallback(
    (message: LiveMessage) => {
      if (message.type === "order.created" || message.type === "order.updated") {
        upsert(message.order);
        highlight(message.order.id);
      } else if (message.type === "order.deleted") {
        duringLoad.current?.set(message.id, "deleted");
        setOrders((current) => current.filter((order) => order.id !== message.id));
      } else if (message.type === "resync") {
        reload();
      }
    },
    [upsert, highlight, reload],
  );

  return { orders, loaded, recent, reload, apply, upsert };
}
